<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :class="rootClass"
  >
    <slot>{{ label }}</slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type PolkaBtnSize = 'medium' | 'large'
export type PolkaBtnColor = 'primary' | 'secondary' | 'success'
export type PolkaBtnHtmlType = 'button' | 'submit' | 'reset'

const props = withDefaults(
  defineProps<{
    /** Подпись, если не используется слот */
    label?: string
    size?: PolkaBtnSize
    color?: PolkaBtnColor
    disabled?: boolean
    type?: PolkaBtnHtmlType
    /** Ключ в fields/submittings (как у RmBtn) */
    field?: string | number
    isLoading?: boolean
  }>(),
  {
    label: '',
    size: 'medium',
    color: 'primary',
    disabled: false,
    type: 'button',
    field: 'empty',
    isLoading: false,
  },
)

const { submittings } = useStoreFields()

const isFieldSubmitting = computed(() => {
  return Boolean(submittings.value?.[props.field])
})

const isBusy = computed(() => isFieldSubmitting.value || props.isLoading)

const isDisabled = computed(() => props.disabled || isBusy.value)

const rootClass = computed(() => {
  const out = ['polka-btn']

  if (isBusy.value) {
    out.push('spin')
  }

  const s = String(props.size).toLowerCase()
  if (s === 'large' || s === 'lg') {
    out.push('polka-btn--lg')
  } else {
    out.push('polka-btn--md')
  }

  const c = String(props.color).toLowerCase()
  if (c === 'secondary') {
    out.push('polka-btn--secondary')
  } else if (c === 'success') {
    out.push('polka-btn--success')
  } else {
    out.push('polka-btn--primary')
  }

  return out
})
</script>

<style lang="sass" scoped>
// Figma: Primary / Secondary / Success × Large / Medium; Default, Hover, Focus, Active, Disabled
.polka-btn
  @apply relative inline-flex appearance-none items-center justify-center overflow-hidden border-0 font-semibold transition-colors duration-150 ease-out
  box-shadow: none

// как у RmBtn: isFieldSubmitting || isLoading
.polka-btn.spin
  @apply relative pointer-events-none
  &:after
    background-image: url('~/assets/svg/spinners/dots.svg')
    pointer-events: none
    @apply h-full bg-center bg-no-repeat inset-0 w-full absolute
    content: ''

.polka-btn--lg
  @apply rounded-2xl px-2 py-3 text-base leading-6

.polka-btn--md
  @apply rounded-lg px-2 py-2 text-sm leading-5

.polka-btn--primary
  @apply bg-blue-500 text-white

.polka-btn--primary:hover:not(:disabled):not(.is-disabled)
  @apply bg-blue-600

.polka-btn--primary:active:not(:disabled):not(.is-disabled)
  @apply bg-blue-600
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2)

.polka-btn--primary:focus
  @apply outline-none

.polka-btn--primary:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-blue-500
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn--primary:hover:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-blue-500

.polka-btn--primary:active:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-blue-600
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn--secondary
  @apply bg-gray-300 text-gray-900

.polka-btn--secondary:hover:not(:disabled):not(.is-disabled)
  @apply bg-gray-400

.polka-btn--secondary:active:not(:disabled):not(.is-disabled)
  @apply bg-gray-400
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2)

.polka-btn--secondary:focus
  @apply outline-none

.polka-btn--secondary:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-gray-300
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn--secondary:hover:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-gray-300

.polka-btn--secondary:active:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-gray-400
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn--success
  @apply bg-green-500 text-white

.polka-btn--success:hover:not(:disabled):not(.is-disabled)
  @apply bg-green-600

.polka-btn--success:active:not(:disabled):not(.is-disabled)
  @apply bg-green-600
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2)

.polka-btn--success:focus
  @apply outline-none

.polka-btn--success:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-green-500
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn--success:hover:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-green-500

.polka-btn--success:active:focus-visible:not(:disabled):not(.is-disabled)
  @apply bg-green-600
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 2px #fff, 0 0 0 3px theme('colors.blue.500')

.polka-btn:disabled,
.polka-btn.is-disabled
  @apply cursor-not-allowed bg-gray-200 text-gray-600 shadow-none

.polka-btn.is-disabled
  pointer-events: none
</style>
